/**
 * Auth-time FIRM dashboard prefetch — the missing half of Paul's cold-start
 * ruling (2026-07-15).
 *
 * WHY THIS EXISTS: `prefetch-dashboard.ts` implemented that ruling for the b2c
 * dashboard only, and said so out loud — "firm staff / auditors / superadmins
 * land on different surfaces and are not prefetched for". Nobody ever built the
 * firm half, so for eleven months the individual landed on a warm backend and
 * the firm ate the full cold start. That is exactly the gap Paul hit on
 * 2026-08-11 ("how have we fixed that for the individual but not the firm?").
 *
 * MEASURED on prod, 2026-08-11, WARM, against a real branded firm:
 *   - nothing fires at all for the first 1.81s (bundle + auth hydration)
 *   - branding + tenant resolve, THEN eleven more calls all start together at
 *     2.38s — they were waiting on that first pair
 *   - three of them take >1.5s each: firm/document-coverage 1658ms,
 *     admin/branding 1619ms, admin/risk-review 1547ms
 *   - total settle 4.04s warm; on a cold Y1 Function App every one of those
 *     eleven pays its own cold start, which is the 30s Paul saw.
 *
 * Login talks only to Supabase (Frankfurt), so while the user spends ~10s on the
 * password and MFA screens our gateway stays cold. This fills the cache during
 * that dead time, using the SAME query keys and fetchers the dashboard hooks use
 * (imported, never re-declared, so they cannot drift apart).
 *
 * Same guard rails as the b2c version: fire-and-forget, failures silent, nothing
 * here may block or slow the auth flow, and callers only invoke it when the
 * post-auth landing surface is actually the firm console.
 */

import type { QueryClient } from '@tanstack/react-query';
import {
  FIRM_DOCUMENT_COVERAGE_QUERY_KEY,
  fetchFirmDocumentCoverage,
} from './document-coverage';
import { ADMIN_RISK_REVIEW_QUERY_KEY, fetchAdminRiskReview } from './risk-review';
import { BRANDING_ME_QUERY_KEY, fetchBranding } from './branding';
import { TRUSTS_LIST_QUERY_KEY, fetchTrustsList } from './trusts';
import { FIRM_CLIENTS_LIST_QUERY_KEY, fetchFirmClients } from './firm-clients';
import { FIRM_BOOK_SUMMARY_QUERY_KEY, fetchFirmBookSummary } from './book-summary';
import {
  canReadFirmSettings,
  canReadWholeBook,
  canUseWorklistTier,
} from '../authz/firm-capabilities';

/**
 * Fire-and-forget prefetch of the firm dashboard's slowest above-the-fold
 * reads. Never throws, never rejects — safe to call inline in the auth flow.
 *
 * `role` is the signed-in role from the new session. Each read is made only
 * for a role the server answers it for (lib/authz/firm-capabilities, held to
 * the backend's tiers by its parity test). Until 4 October 2026 all six fired
 * for every firm role, so a relationship manager signing in drew three 403s
 * (book summary, document coverage, branding) before the first screen painted.
 */
export function prefetchAdminDashboard(
  client: QueryClient,
  role: string | null | undefined,
): void {
  try {
    // All four are independent tenant-level reads — fire them together rather
    // than reproducing the waterfall this module exists to remove.
    void client.prefetchQuery({
      // FIRST, because /admin/client-book is where firm staff actually land —
      // `landingRouteForRole` never returns /admin/dashboard at all, which the
      // typechecker caught when this module was first written against the wrong
      // assumption. The client book is the screen a firm user waits on.
      queryKey: FIRM_CLIENTS_LIST_QUERY_KEY,
      queryFn: ({ signal }) => fetchFirmClients(signal),
    });
    if (canReadWholeBook(role)) {
      void client.prefetchQuery({
        // The dashboard headline "N trusts under administration" is read off this
        // one, and Dashboard is the first nav item, so it is worth having warm.
        queryKey: FIRM_DOCUMENT_COVERAGE_QUERY_KEY,
        queryFn: ({ signal }) => fetchFirmDocumentCoverage(signal),
      });
      void client.prefetchQuery({
        // Fans out across every granted client tenant, so it is one of the two
        // slowest reads on the dashboard and the one three cards depend on. Worth
        // starting while the user is still on the MFA screen.
        queryKey: FIRM_BOOK_SUMMARY_QUERY_KEY,
        queryFn: ({ signal }) => fetchFirmBookSummary(signal),
      });
    }
    if (canReadFirmSettings(role)) {
      void client.prefetchQuery({
        queryKey: BRANDING_ME_QUERY_KEY,
        queryFn: ({ signal }) => fetchBranding(signal),
      });
    }
    if (canUseWorklistTier(role)) {
      void client.prefetchQuery({
        queryKey: ADMIN_RISK_REVIEW_QUERY_KEY,
        queryFn: ({ signal }) => fetchAdminRiskReview(signal),
      });
    }
    void client.prefetchQuery({
      queryKey: TRUSTS_LIST_QUERY_KEY,
      queryFn: ({ signal }) => fetchTrustsList(signal),
    });
  } catch {
    // Defensive: nothing in the prefetch path may ever disturb auth.
  }
}
